import { Injectable } from '@angular/core';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ProjectModel } from '../../model/project.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, mergeMap } from 'rxjs/operators';
import { LoadProjectInfoService } from '../../service/load-project-info.service';
import { ActivateUniformDirectPrintingOperation } from './activate-uniform-direct-printing-operation';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { DeactivateUniformDirectPrintingOperation } from './deactivate-uniform-direct-printing-operation';
import { UniformDirectPrintingModel } from './uniform-direct-printing.model';
import { UniformDirectPrintingService } from './uniform-direct-printing.service';

@Injectable({
  providedIn: 'root',
})
export class DirectPrintingService {

  private readonly _uniformDirectPrintingModel: UniformDirectPrintingModel;

  public get useUniformDirectPrintingFontSize(): boolean {
    return this._uniformDirectPrintingModel.useUniformDirectPrintingFontSize;
  }

  public get uniformFontSize(): number | undefined {
    return this._uniformDirectPrintingModel.uniformFontSize;
  }

  constructor(
    projectModel: ProjectModel,
    loadProjectInfoService: LoadProjectInfoService,
    private readonly uniformDirectPrintingService: UniformDirectPrintingService,
    private readonly applicationRefreshService: ApplicationRefreshService,
    private readonly undoRedoManager: UndoRedoManager,
  ) {
    this._uniformDirectPrintingModel = new UniformDirectPrintingModel(projectModel);
    projectModel.currentProject$.pipe(takeUntilDestroyed()).subscribe(() => {
      this._uniformDirectPrintingModel.uniformFontSize = undefined;
    });
    loadProjectInfoService.projectLoaded$.pipe(
      takeUntilDestroyed(),
      filter((data) => data.success && this.useUniformDirectPrintingFontSize),
      mergeMap(() => this.uniformDirectPrintingService.loadUniformFontSize()),
    ).subscribe((uniformFontSize: number | undefined) => {
      this._uniformDirectPrintingModel.uniformFontSize = uniformFontSize;
    });
  }

  public async activateUniformDirectPrintingIfNecessary(): Promise<void> {
    const uniformFontSize = await this.uniformDirectPrintingService.loadUniformFontSize();
    if (this.uniformFontSize !== uniformFontSize) {
      await this.activateUniformDirectPrinting();
    }
  }

  public async activateUniformDirectPrinting(): Promise<void> {
    const operation = new ActivateUniformDirectPrintingOperation(
      this.uniformDirectPrintingService,
      this._uniformDirectPrintingModel,
      this.applicationRefreshService,
    );
    await operation.performRedo();
    this.undoRedoManager.pushUndo(operation);
  }

  public async deactivateUniformDirectPrinting(): Promise<void> {
    const operation = new DeactivateUniformDirectPrintingOperation(
      this.uniformDirectPrintingService,
      this._uniformDirectPrintingModel,
      this.applicationRefreshService,
    );
    await operation.performRedo();
    this.undoRedoManager.pushUndo(operation);
  }
}
